import { FormField, FormItem, FormMessage } from "@/ui/form";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from "@cmp-src/primitives/Select";
import { SelectUsersAsync } from "@/features/users/components/SelectUsersAsync";
import { SaveReservationData } from "@/features/reserve/utils/utils-reserve";
import { useAuthenticated } from "@gettactic/hooks";
import { FieldErrors, UseFormReturn } from "react-hook-form";
import { Alert } from "@gettactic/components/src/data-display/Alert/Alert";
import { delegatedPermissionPartials } from "@gettactic/helpers";

type Props = {
  form: UseFormReturn<any>;
  errors: FieldErrors<SaveReservationData>;
  defaultValue?: { value: string };
  readonly?: boolean;
  /** The selected organizer hasn't granted Outlook delegated access, so saving will fail. */
  showDelegatedAccessWarning?: boolean;
};
export function ReserveFieldOrganizer({
  form,
  errors,
  defaultValue,
  readonly = false,
  showDelegatedAccessWarning = false
}: Props) {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const isTeamManager =
    !!authenticatedUser?.user?.permissions.includes(
      "schedule:others:managed:add"
    ) && !authenticatedUser?.user?.permissions.includes("schedule:others:add");

  return (
    <>
      <FormField
        control={form.control}
        name="organizer"
        render={({ field }) => (
          <FormItem>
            <SelectUsersAsync
              defaultValue={[defaultValue?.value ?? ""]}
              managed={isTeamManager}
              setInitial={(x) => {
                // Before we can set default values we need to do an async call to retrieve IDs.
                // The default re-resolves whenever it changes — for a new reservation it
                // follows the first attendee — so it may only seed an empty field, never
                // overwrite the organizer someone already picked.
                if (form.getValues("organizer")) return;
                form.setValue("organizer", x[0], { shouldValidate: false });
              }}
              onChange={field.onChange}
              value={field.value}
              className="w-full rounded-md !border-none rounded-v2"
              classNamePrefix="react-select"
              placeholder={"Select the Organizer"}
              isMulti={false}
              readonly={readonly}
            />
            <FormMessage />
          </FormItem>
        )}
      />
      {showDelegatedAccessWarning && (
        <Alert
          className="mt-2"
          variant="warning"
          canDismiss={false}
          alertTitle={delegatedPermissionPartials.organizerDescription}
          alertMessage=""
        />
      )}
    </>
  );
}

/**
 * <Controller
        key={JSON.stringify([defaultValue])}
        control={control}
        name="organizer"
        render={({ field: { onChange, value } }) => (
          <SelectUsersAsync
            managed={isTeamManager}
            setInitial={(x) => {
              // Before we can set default values we need to do an async call to retrieve IDs.
              setValue('organizer', x[0], { shouldValidate: false });
            }}
            onChange={onChange}
            value={value}
            className="w-full rounded-md border-neutral-300"
            classNamePrefix="react-select"
            placeholder={`Select the Organizer`}
            isMulti={false}
            readonly={readonly}
          />
        )}
      />
 */
